import { useState } from 'react'
import { X, Sparkles } from 'lucide-react'

const HYGIENE_ITEMS = [
  { key: 'sep_seeing', label: 'I separated what I saw or heard from what I inferred.' },
  { key: 'standpoint', label: 'I have seriously considered at least one other standpoint.' },
  { key: 'falsify', label: 'I can name one thing that would change my current explanation.' },
  { key: 'identity', label: 'This description would not let someone outside our circle point to the exact people or household.' },
  { key: 'power', label: 'I have considered whose knowledge or authority is being centered or sidelined here.' },
  { key: 'one_instance', label: 'I am holding this as one instance, not a class of people or villages.' },
]

export default function Composer({ onClose, onSave, inCircle, circleName, onAiAssumptions, salienceOptions }: any) {
  const [description, setDescription] = useState('')
  const [salience, setSalience] = useState<string[]>([])
  const [salienceFree, setSalienceFree] = useState('')
  const [anomaly, setAnomaly] = useState(0.55)
  const [explanation, setExplanation] = useState('')
  const [hygiene, setHygiene] = useState<Record<string, { checked: boolean; note?: string }>>({})
  const [aiAssumptions, setAiAssumptions] = useState<any>(null)
  const [loadingAi, setLoadingAi] = useState(false)

  function toggleSalience(tag: string) {
    setSalience(s => s.includes(tag) ? s.filter(t => t !== tag) : [...s, tag])
  }

  function toggleHygiene(key: string) {
    setHygiene(h => ({
      ...h,
      [key]: { checked: !h[key]?.checked, note: h[key]?.note }
    }))
  }

  function setHygieneNote(key: string, note: string) {
    setHygiene(h => ({ ...h, [key]: { checked: h[key]?.checked || false, note } }))
  }

  async function runAiAssumptions() {
    if (!description) return
    setLoadingAi(true)
    try {
      const res = await onAiAssumptions(description, explanation)
      setAiAssumptions(res)
    } catch {}
    setLoadingAi(false)
  }

  function acceptRephrase() {
    if (aiAssumptions?.rephrased) {
      setDescription(aiAssumptions.rephrased)
      setAiAssumptions(null)
    }
  }

  async function handleSave() {
    if (!description.trim()) return
    const payload = {
      description: description.trim(),
      salienceTags: salience,
      salienceFree: salienceFree.trim() || null,
      anomaly,
      explanation: explanation.trim() || null,
      hygiene,
      visibility: inCircle ? 'circle' : 'private',
    }
    await onSave(payload)
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[80] flex items-start justify-center pt-8 pb-8 px-3 overflow-auto" onClick={onClose}>
      <div className="paper w-full max-w-2xl rounded-2xl p-5 md:p-7 composer" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div>
            <div className="font-serif text-2xl">Mark a moment</div>
            <div className="text-sm text-[#5c4633]">What stayed with you? What felt uncertain or important?</div>
            {inCircle && <div className="text-xs mt-1 text-[#4a3f6b]">This will be visible in {circleName}</div>}
          </div>
          <button onClick={onClose} className="text-[#5c4633]"><X className="w-5 h-5" /></button>
        </div>

        {/* Description */}
        <div className="mt-5">
          <div className="text-xs uppercase tracking-widest text-[#5c4633] mb-1">What presented itself</div>
          <textarea 
            value={description} 
            onChange={e => setDescription(e.target.value)} 
            placeholder="Describe the situation or moment as it presented itself. What actually happened or was said? Write so that even people close to it would not be identifiable to an outsider."
            className="w-full h-36 p-4 rounded-xl border border-[#d4c9b8] bg-white font-serif text-[15px] leading-snug" 
          />
          <button onClick={runAiAssumptions} disabled={loadingAi || !description} className="mt-2 text-xs flex items-center gap-1 text-[#4a3f6b] hover:text-[#3f2e1e]">
            <Sparkles className="w-3.5 h-3.5" /> Surface assumptions (AI scaffold — you decide what to keep)
          </button>
          {aiAssumptions && (
            <div className="mt-2 p-3 bg-[#f4f0e8] rounded text-sm border border-[#d4c9b8]">
              {aiAssumptions.rephrased && (
                <div>
                  <div className="text-xs text-[#5c4633]">Suggested rephrasing (makes assumptions more visible):</div>
                  <div className="font-serif my-1">{aiAssumptions.rephrased}</div>
                  <button onClick={acceptRephrase} className="text-xs underline">Use this phrasing</button>
                </div>
              )}
              {aiAssumptions.assumptions?.length > 0 && (
                <div className="mt-2 text-xs">
                  <div className="text-[#5c4633]">Assumptions that seem to be carried:</div>
                  <ul className="list-disc pl-4 mt-1 text-[#4a3f6b]">
                    {aiAssumptions.assumptions.map((a: string, i: number) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Salience */}
        <div className="mt-5">
          <div className="text-xs uppercase tracking-widest text-[#5c4633] mb-2">What made this stay with you?</div>
          <div className="flex flex-wrap gap-1.5">
            {salienceOptions.map((tag: string) => (
              <div key={tag} onClick={() => toggleSalience(tag)} className={`salience-chip ${salience.includes(tag) ? 'selected' : ''}`}>{tag.replace(/-/g, ' ')}</div>
            ))}
          </div>
          <input value={salienceFree} onChange={e=>setSalienceFree(e.target.value)} placeholder="or describe in your own words..." className="mt-2 w-full text-sm p-2 rounded border border-[#d4c9b8] bg-white" />
        </div>

        {/* Anomaly */}
        <div className="mt-5">
          <div className="flex justify-between text-xs uppercase tracking-widest text-[#5c4633] mb-1.5">
            <div>How far outside the usual pattern?</div>
            <div className="font-mono tabular-nums">{Math.round(anomaly * 100)}%</div>
          </div>
          <input type="range" min={0} max={1} step={0.01} value={anomaly} onChange={e => setAnomaly(parseFloat(e.target.value))} className="w-full accent-[#8b5e3c]" />
          <div className="flex justify-between text-[10px] text-[#5c4633] mt-0.5">
            <div>Barely unusual</div>
            <div>Completely outside the pattern I know</div>
          </div>
        </div>

        {/* Current explanation */}
        <div className="mt-5">
          <div className="text-xs uppercase tracking-widest text-[#5c4633] mb-1">Your best sense right now (held lightly)</div>
          <textarea value={explanation} onChange={e=>setExplanation(e.target.value)} placeholder="What do you think is going on? This can change. No need to be certain." className="w-full h-20 p-3 rounded border border-[#d4c9b8] bg-white text-sm" />
        </div>

        {/* Epistemic hygiene — always visible, never blocking */}
        <div className="mt-6 pt-5 border-t border-[#d4c9b8]">
          <div className="text-xs uppercase tracking-widest text-[#5c4633] mb-2">Epistemic hygiene (for you and the commons)</div>
          <div className="space-y-2 text-sm">
            {HYGIENE_ITEMS.map(item => {
              const checked = !!hygiene[item.key]?.checked
              return (
                <div key={item.key} className={`hygiene-item rounded px-3 py-2 flex gap-2 ${checked ? 'checked' : ''}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleHygiene(item.key)} className="mt-1" />
                  <div className="flex-1">
                    <div>{item.label}</div>
                    <input 
                      placeholder="private note (optional)" 
                      className="text-xs mt-1 w-full bg-transparent border-b border-[#d4c9b8] focus:border-[#8b5e3c] placeholder:text-[#8b5e3c]/50"
                      value={hygiene[item.key]?.note || ''} 
                      onChange={e => setHygieneNote(item.key, e.target.value)} 
                    />
                  </div>
                </div>
              )
            })}
          </div>
          <div className="text-[10px] text-[#5c4633] mt-2">These are recorded with the observation. They are not a gate. They are part of the interpretive record.</div>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={handleSave} disabled={!description.trim()} className="flex-1 py-3 rounded-xl bg-[#3f2e1e] text-[#f8f5f0] font-medium disabled:opacity-40">Mark this moment in the field</button>
          <button onClick={onClose} className="px-6 py-3 rounded-xl border border-[#d4c9b8]">Cancel</button>
        </div>

        <div className="text-center text-[10px] text-[#5c4633] mt-4">After you save you will be invited to close the loop with a restored orientation note. That is where the metabolizing happens.</div>
      </div>
    </div>
  )
}
